import { useApolloClient } from '@apollo/client';
import { TEventBusEventCallback } from '@xometry/event-bus';
import {
  EMessengerChatSourceType,
  EMessengerEventBusEvents,
  IEventBusMessengerAttachment,
  IEventBusMessengerEvents,
  MessengerEventBus,
  TEventBusMessengerRedirectToSourcePageEventPayload,
  useMonitoringClient,
} from '@xometry/ui';
import { AppContext } from 'contexts/AppContext';
import { cloneDeep } from 'es-toolkit/compat';
import { PreviewsSchema } from 'interfaces';
import { QMessengerCounter } from 'interfaces/graphql/messengerCounter';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { routes } from 'routes';
import { routesApi } from 'routes/api';
import { gshObjectsRoutes } from 'routes/gsh';
import { useStore } from 'stores/RootStore';
import { MessengerCounterDocument } from 'utils/graphql/queries/__generated__/messengerCounter';

import { downloader as useDownload, useGroupDownload } from './useDownload';

const getRouteBySource = ({
  dealId,
  sourceId,
  sourceType,
  parentId,
  externalId,
}: TEventBusMessengerRedirectToSourcePageEventPayload): string | undefined => {
  if (
    !dealId &&
    ![
      EMessengerChatSourceType.CUSTOMER,
      EMessengerChatSourceType.PARTNER,
      EMessengerChatSourceType.MASTER_SHIPPING,
      EMessengerChatSourceType.BILLING_ACCOUNT,
      EMessengerChatSourceType.GSH_JOB_OFFER,
    ].includes(sourceType)
  ) {
    return;
  }

  if (sourceType === EMessengerChatSourceType.GSH_JOB_OFFER) {
    if (!externalId) return;

    const gshJobOfferRoute = gshObjectsRoutes[EMessengerChatSourceType.GSH_JOB_OFFER];

    return gshJobOfferRoute(externalId);
  }

  const dealPath = routes.dealPath(String(dealId));

  if (!sourceType) {
    return dealPath;
  }

  if (!sourceId) return;

  const routesMap: Record<EMessengerChatSourceType, string | undefined> = {
    [EMessengerChatSourceType.CUSTOMER]: routes.personPath(sourceId),
    [EMessengerChatSourceType.PARTNER]: routes.providerPath(sourceId),
    [EMessengerChatSourceType.DEAL]: dealPath,
    [EMessengerChatSourceType.QUALITY_CONTROL]: routes.editDealQualityControlPath(String(dealId), sourceId),
    [EMessengerChatSourceType.ORDER_CONFIRMATION]: routes.orderConfirmationPath(String(dealId)),
    [EMessengerChatSourceType.PROVIDER_ORDER]: routes.editProviderOrderPath(String(dealId), sourceId),
    [EMessengerChatSourceType.PART]: routes.dealPartPath(String(dealId), sourceId),
    [EMessengerChatSourceType.CLAIM]: routes.editDealClaimPath(String(dealId), sourceId),
    [EMessengerChatSourceType.SHIPPING]: routes.editShippingPath(String(dealId), sourceId),
    [EMessengerChatSourceType.BILLING_ACCOUNT]: routes.billingAccountPath(sourceId),
    [EMessengerChatSourceType.MASTER_SHIPPING]: routes.editMasterShippingPath(sourceId),
    [EMessengerChatSourceType.STORAGE]: routes.editStoragePath(String(dealId), sourceId),
    [EMessengerChatSourceType.OUTCOMING_INVOICE]: routes.editOutcomingInvoicePath(String(dealId), sourceId),
    [EMessengerChatSourceType.INCOMING_INVOICE]: routes.editDealIncomingInvoicePath(String(dealId), sourceId),
    [EMessengerChatSourceType.PAYMENT]: routes.paymentPath(String(dealId), sourceId),
    [EMessengerChatSourceType.PAYOUT]: routes.payoutPath(String(dealId), sourceId),
    [EMessengerChatSourceType.JOB]: routes.jobPath(String(dealId), sourceId),
    [EMessengerChatSourceType.JOB_OFFER]: parentId ? routes.jobOfferPath(String(dealId), parentId, sourceId) : dealPath,
    [EMessengerChatSourceType.ENQUIRY]: routes.enquiryEditPath(String(dealId), sourceId),
    [EMessengerChatSourceType.ENQUIRY_PART]: routes.enquiryEditPath(String(dealId), sourceId),
    [EMessengerChatSourceType.ENQUIRY_RFQ]: undefined,
    [EMessengerChatSourceType.GSH_JOB_OFFER]: undefined,
    [EMessengerChatSourceType.RFQ]: routes.rfqEditPath(String(dealId), sourceId),
    [EMessengerChatSourceType.RFQ_OFFER]: parentId ? routes.rfqOfferPath(String(dealId), parentId, sourceId) : dealPath,
  };

  return routesMap[sourceType];
};

export const useListenCommonMessengerEvents = () => {
  const { messengerEventBus } = useContext(AppContext);
  const history = useHistory();
  const apolloClient = useApolloClient();
  const downloader = useDownload();
  const { download: groupDownload } = useGroupDownload();
  const monitoringClient = useMonitoringClient();

  const {
    previewsStore: { loadPreview, setSchema, openPreview: setOpenPreview },
  } = useStore();

  const openPreview = useCallback(
    (params?: { current: number; attachments: IEventBusMessengerAttachment[] }) => {
      const { current = 0, attachments } = params || {};

      try {
        const schema: PreviewsSchema = {
          documents: [
            {
              bucketName: 'attachments',
              items: (attachments || []).map((item) => ({
                id: +item.fileId,
                name: item.fileName,
                context: 'attachments   ',
                isCleaned: false,
                url: item.preview || '',
              })),
            },
          ],
          drawings: [],
        };

        loadPreview((attachments || [])[current].fileId)
          .then(() => {
            setSchema(schema);
            setOpenPreview();
          })
          .catch((e: unknown) => {
            monitoringClient.captureError(e instanceof Error ? e : new Error(String(e)), { level: 'warning' });
          });
      } catch (e) {
        console.error(e);
      }
    },
    [loadPreview, monitoringClient, setOpenPreview, setSchema],
  );

  const handleOpenPreview: TEventBusEventCallback<
    IEventBusMessengerEvents,
    EMessengerEventBusEvents.OPEN_FILE_PREVIEW
  > = useCallback(
    ({ current, files }) => {
      if (typeof current === 'number' && files.length > 0) {
        openPreview({ current, attachments: files });
      }
    },
    [openPreview],
  );

  const handleRedirectToSourcePage: TEventBusEventCallback<
    IEventBusMessengerEvents,
    EMessengerEventBusEvents.REDIRECT_TO_SOURCE_PAGE
  > = useCallback(
    (messageSource) => {
      const route = getRouteBySource(messageSource);

      if (!route) return;

      if (messageSource.sourceType === EMessengerChatSourceType.GSH_JOB_OFFER) {
        window.open(route, '_blank', 'noopener,noreferrer');

        return;
      }

      const currentRoute = `${history.location.pathname}${history.location.search}${history.location.hash}`;

      let targetRoute = route;

      try {
        const normalizedUrl = new URL(route, window.location.origin);

        targetRoute = `${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`;
      } catch {
        // keep original route if URL constructor fails
      }

      if (targetRoute === currentRoute) return;

      history.push(route);
    },
    [history],
  );

  const handleDownload: TEventBusEventCallback<IEventBusMessengerEvents, EMessengerEventBusEvents.DOWNLOAD_FILE> =
    useCallback(
      async ({ fileId, fileName, onSuccess, onError }) => {
        try {
          await downloader(routesApi.attachedFileDownloadPath(fileId), fileName);

          if (typeof onSuccess === 'function') {
            onSuccess();
          }
        } catch {
          if (typeof onError === 'function') {
            onError();
          }
        }
      },
      [downloader],
    );

  const handleDownloadGroupFiles: TEventBusEventCallback<
    IEventBusMessengerEvents,
    EMessengerEventBusEvents.DOWNLOAD_FILES
  > = useCallback(
    async ({ uuidIds, fileName, onSuccess, onError }) => {
      try {
        await groupDownload(uuidIds, fileName);

        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      } catch {
        if (typeof onError === 'function') {
          onError();
        }
      }
    },
    [groupDownload],
  );

  const handleSetCounters: TEventBusEventCallback<IEventBusMessengerEvents, EMessengerEventBusEvents.SET_COUNTER> =
    useCallback(
      (newCounter) => {
        // https://github.com/apollographql/apollo-client/issues/3909#issuecomment-449363099
        const queryData = cloneDeep(apolloClient.readQuery<QMessengerCounter>({ query: MessengerCounterDocument }));

        apolloClient.writeQuery<QMessengerCounter>({
          query: MessengerCounterDocument,
          data: { messengerCounter: { ...queryData?.messengerCounter, ...newCounter } },
        });
      },
      [apolloClient],
    );

  const addEventListeners = useCallback(
    (eb: MessengerEventBus) => {
      eb.addEventListener(EMessengerEventBusEvents.OPEN_FILE_PREVIEW, handleOpenPreview);
      eb.addEventListener(EMessengerEventBusEvents.REDIRECT_TO_SOURCE_PAGE, handleRedirectToSourcePage);
      eb.addEventListener(EMessengerEventBusEvents.DOWNLOAD_FILE, handleDownload);
      eb.addEventListener(EMessengerEventBusEvents.DOWNLOAD_FILES, handleDownloadGroupFiles);
      eb.addEventListener(EMessengerEventBusEvents.SET_COUNTER, handleSetCounters);
    },
    [handleDownload, handleDownloadGroupFiles, handleOpenPreview, handleRedirectToSourcePage, handleSetCounters],
  );

  const removeEventListeners = useCallback(
    (eb: MessengerEventBus) => {
      eb.removeEventListener(EMessengerEventBusEvents.OPEN_FILE_PREVIEW, handleOpenPreview);
      eb.removeEventListener(EMessengerEventBusEvents.REDIRECT_TO_SOURCE_PAGE, handleRedirectToSourcePage);
      eb.removeEventListener(EMessengerEventBusEvents.DOWNLOAD_FILE, handleDownload);
      eb.removeEventListener(EMessengerEventBusEvents.DOWNLOAD_FILES, handleDownloadGroupFiles);
      eb.removeEventListener(EMessengerEventBusEvents.SET_COUNTER, handleSetCounters);
    },
    [handleDownload, handleDownloadGroupFiles, handleOpenPreview, handleRedirectToSourcePage, handleSetCounters],
  );

  useEffect(() => {
    if (messengerEventBus) {
      addEventListeners(messengerEventBus);

      return () => {
        removeEventListeners(messengerEventBus);
      };
    }
  }, [addEventListeners, messengerEventBus, removeEventListeners]);
};
